/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Toster from "../../../common/Toster";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createRentalItem,
  getRentalItemById,
  updateRentalItem,
  storeRentalItemPricing,
  updateRentalItemPricing,
  getRentalTerms,
  approveOrRejectRental,
} from "../rentalService";
import RentalGeneralInfo from "../AddRentalTabs/RentalGeneralInfo";
import RentalGallery from "../AddRentalTabs/RentalGallery";
import RentalPricing from "../AddRentalTabs/RentalPricing";
import RentalBlockedDates from "../AddRentalTabs/RentalBlockedDates";
import RentalFAQ from "../AddRentalTabs/RentalFAQ";
import RentalInclusions from "../AddRentalTabs/RentalInclusions";
import RentalExclusions from "../AddRentalTabs/RentalExclusions";
import RentalTerms from "../AddRentalTabs/RentalTerms";
import {
  BUILDER_SECTIONS,
  getBuilderResumeSectionId,
  isSectionCompleted,
} from "./rentalBuilderConfig";
import {
  RENTAL_STAGES,
  RENTAL_MAX_STAGE,
  mergeGeneralSavePayload,
  getTabStage,
} from "./rentalFormUtils";
import {
  getCategoryPricingBasis,
  isRentalPricingComplete,
  normalizeRentalPricingPayload,
  RENTAL_PRICING_BASIS,
} from "../../../../config/rentalCategoryFormConfig";
import { serializePickupLocationsForSave } from "../rentalPickupLocationUtils";
import RentalBuilderNav from "../RentalBuilderNav";
import RentalCompletionTracker from "../RentalCompletionTracker";
import PackageActionBar from "../../package/PackageActionBar";
import { usePackageSectionSpy } from "../../package/usePackageSectionSpy";
import { useSectionDirtyGuard } from "../../../../utils/useSectionDirtyGuard";
import UnsavedSectionDialog from "../../../common/UnsavedSectionDialog";

const SCROLL_OFFSET = 160;

const AdminRentalBuilder = ({
  open = true,
  onClose,
  exitPath = "/rentals",
  rentalId: rentalIdProp,
  supplierId,
  showApprovalActions = false,
  backLabel = "Back to Rentals",
}) => {
  const [exitTo, setExitTo] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [generalInfoData, setGeneralInfoData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savingSectionId, setSavingSectionId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [validationErrors, setValidationErrors] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isGeneralValid, setIsGeneralValid] = useState(false);
  const [stepValidity, setStepValidity] = useState({});
  const [showToster, setShowToster] = useState(false);
  const [tosterMessage, setTosterMessage] = useState("");
  const [tosterType, setTosterType] = useState("success");
  const [toastKey, setToastKey] = useState(0);
  const [createdRentalId, setCreatedRentalId] = useState(null);
  const saveHandlersRef = useRef({});
  const generalFormDataRef = useRef(null);
  const pricingFormDataRef = useRef(null);
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const isScrollingRef = useRef(false);
  const pinnedSectionRef = useRef(null);
  const manualSectionRef = useRef(null);
  const resumedRef = useRef(false);
  const skipAutoAdvanceRef = useRef(false);

  const currentId = rentalIdProp || createdRentalId;
  const isEditingExisting = Boolean(rentalIdProp);
  const queryClient = useQueryClient();
  const visibleSections = BUILDER_SECTIONS;
  const sectionIds = useMemo(() => visibleSections.map((s) => s.id), []);

  const { data: rentalData, isLoading, error } = useQuery({
    queryKey: ["rental-admin", currentId],
    queryFn: () => getRentalItemById(currentId),
    enabled: open && !!currentId,
  });

  const rental = rentalData?.data;

  const rawSavedStage = Number(rentalData?.data?.current_stage) || RENTAL_STAGES.GENERAL;
  const savedStage = Math.min(RENTAL_MAX_STAGE, Math.max(RENTAL_STAGES.GENERAL, rawSavedStage));

  const { data: termsData } = useQuery({
    queryKey: ["rentalTerms", currentId],
    queryFn: () => getRentalTerms(currentId),
    enabled: open && !!currentId,
  });

  const hasTerms = Boolean(termsData?.data?.content);

  const activeSectionLabel =
    BUILDER_SECTIONS.find((item) => item.id === activeTab)?.name || "Section";
  const activeSection = BUILDER_SECTIONS.find((item) => item.id === activeTab);
  const actionBarSaveLabel =
    activeTab === "terms"
      ? hasTerms || stepValidity.terms
        ? "Save & Finish"
        : "Skip & Finish"
      : activeSection?.essential === false
      ? "Skip"
      : undefined;

  const {
    dialogOpen: unsavedDialogOpen,
    requestSectionNavigation,
    cancelPendingNavigation,
    completePendingNavigation,
    clearSectionDirty,
    getSectionCaptureProps,
  } = useSectionDirtyGuard({
    activeSectionId: activeTab,
    isLoading: isSubmitting,
  });

  useEffect(() => {
    if (open) {
      setCreatedRentalId(null);
      setGeneralInfoData(null);
      setPricingData(null);
      setActiveTab("general");
      setValidationErrors(null);
      setSubmitAttempted(false);
      setIsGeneralValid(false);
      setStepValidity({});
      setLastSavedAt(null);
      setShowToster(false);
      setTosterMessage("");
      setTosterType("success");
      setToastKey(0);
      resumedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !rentalIdProp || resumedRef.current) return;
    if (!rentalData?.data) return;
    resumedRef.current = true;
    const sectionId = getBuilderResumeSectionId(savedStage);
    setTimeout(() => scrollToSection(sectionId), 100);
  }, [open, rentalIdProp, rentalData, savedStage]);

  const showToast = (type, message) => {
    setTosterType(type);
    setTosterMessage(message);
    setToastKey((k) => k + 1);
    setShowToster(true);
  };

  const createMutation = useMutation({
    mutationFn: (formData) => {
      if (supplierId && formData instanceof FormData && !formData.has("supplier_id")) {
        formData.append("supplier_id", String(supplierId));
      }
      return createRentalItem(formData);
    },
    onSuccess: (data) => {
      const newId = data.data.id;
      setCreatedRentalId(newId);
      showToast("success", "Rental created successfully!");
      queryClient.invalidateQueries(["rentals-admin"]);
      queryClient.invalidateQueries(["rental-admin", newId]);
      scrollToSection("gallery");
    },
    onError: (error) => {
      showToast("error", error.response?.data?.message || "Failed to create rental");
      setValidationErrors(error.response?.data?.errors || {});
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateRentalItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rentals-admin"]);
      queryClient.invalidateQueries(["rental-admin", currentId]);
    },
    onError: (error) => {
      showToast("error", error.response?.data?.message || "Failed to update rental");
      setValidationErrors(error.response?.data?.errors || {});
    },
  });

  const pricingUpdateMutation = useMutation({
    mutationFn: ({ id, data, isCreate }) =>
      isCreate ? storeRentalItemPricing(id, data) : updateRentalItemPricing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rentals-admin"]);
      queryClient.invalidateQueries(["rental-admin", currentId]);
    },
    onError: (error) => {
      showToast("error", error.response?.data?.message || "Failed to update rental");
      setValidationErrors(error.response?.data?.errors || {});
    },
  });

  const stageMutation = useMutation({
    mutationFn: ({ id, nextStage }) => {
      const payload = { current_stage: String(nextStage) };
      if (Number(nextStage) === RENTAL_MAX_STAGE && !isEditingExisting) {
        payload.status = 1;
        payload.is_approved = 0;
        payload.is_active = 1;
      }
      return updateRentalItem(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["rental-admin", currentId]);
      queryClient.invalidateQueries(["rentals-admin"]);
    },
  });

  const approveMutation = useMutation({
    mutationFn: (is_approved) =>
      approveOrRejectRental({ rental_item_id: Number(currentId), is_approved }),
    onSuccess: () => {
      queryClient.invalidateQueries(["rental-admin", currentId]);
      queryClient.invalidateQueries(["rentals-admin"]);
      showToast("success", "Approval updated.");
    },
    onError: (e) => {
      showToast("error", e?.response?.data?.message || "Failed to update approval.");
    },
  });

  const handleSpyActiveChange = useCallback((sectionId) => {
    if (manualSectionRef.current) return;
    if (isScrollingRef.current || pinnedSectionRef.current) return;
    setActiveTab(sectionId);
  }, []);

  const scrollToSection = (sectionId, { userInitiated = false } = {}) => {
    if (userInitiated) {
      manualSectionRef.current = sectionId;
    }
    setActiveTab(sectionId);
    pinnedSectionRef.current = sectionId;
    isScrollingRef.current = true;

    const el = sectionRefs.current[sectionId];
    if (el) {
      const behavior = userInitiated ? "auto" : "smooth";
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const top = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
          window.scrollTo({ top: Math.max(0, top), behavior });
        });
      });
    }

    const scrollLockMs = userInitiated ? 1200 : 900;
    window.setTimeout(() => {
      isScrollingRef.current = false;
      setActiveTab(sectionId);
      window.setTimeout(() => {
        if (pinnedSectionRef.current === sectionId) {
          pinnedSectionRef.current = null;
        }
        if (userInitiated && manualSectionRef.current === sectionId) {
          manualSectionRef.current = null;
        }
      }, 200);
    }, scrollLockMs);
  };

  const scrollAfterSave = (sectionId) => {
    if (skipAutoAdvanceRef.current) return;
    scrollToSection(sectionId, { userInitiated: true });
  };

  usePackageSectionSpy({
    sectionIds,
    sectionRefs,
    isScrollingRef,
    pinnedSectionRef,
    onActiveChange: handleSpyActiveChange,
    offset: SCROLL_OFFSET,
    enabled: open,
  });

  const registerStepSave = (tabId) => (fn) => {
    if (typeof fn === "function") saveHandlersRef.current[tabId] = fn;
  };

  const registerStepValidity = (tabId) => (isValid) => {
    setStepValidity((prev) => ({ ...prev, [tabId]: Boolean(isValid) }));
  };

  const isStepComplete = (tabId) => {
    switch (tabId) {
      case "general":
        return isGeneralValid;
      case "gallery":
        return true;
      case "pricing":
        return isRentalPricingComplete(
          pricingData,
          getCategoryPricingBasis(rentalData?.data?.category)
        );
      case "availability":
      case "faq":
      case "inclusions":
      case "exclusions":
        return true;
      case "terms":
        return true;
      default:
        return true;
    }
  };

  const isSectionDisabled = (item) => {
    if (isEditingExisting) return false;
    if (!currentId && item.id !== "general") return true;
    if (item.stage > savedStage) return true;
    return false;
  };

  const getSectionDisabledMessage = (item, isDisabledByNoId) => {
    if (isDisabledByNoId) {
      return "Save General Information first to unlock this section.";
    }
    return "Complete the previous sections to unlock this section.";
  };

  const isSaveDisabled = (sectionId) => {
    if (isSubmitting || createMutation.isPending || updateMutation.isPending || pricingUpdateMutation.isPending) {
      return true;
    }
    if (sectionId === "general") return !isGeneralValid || (!currentId && (!supplierId || String(supplierId) === ""));
    if (sectionId === "terms") return false;
    return !isStepComplete(sectionId);
  };

  const handleSectionSave = async (sectionId, options = {}) => {
    skipAutoAdvanceRef.current = Boolean(options.skipAutoAdvance);
    setIsSubmitting(true);
    setSavingSectionId(sectionId);
    setValidationErrors(null);
    if (sectionId === "general") setSubmitAttempted(true);

    const sectionName = visibleSections.find((s) => s.id === sectionId)?.name || "Section";

    try {
      if (sectionId === "general") {
        if (!isGeneralValid) {
          showToast("error", "Please fill required fields.");
          return;
        }

        const nextStage = Math.max(savedStage, RENTAL_STAGES.GALLERY);
        const serverRow = rentalData?.data || {};
        const latestGeneral = generalFormDataRef.current ?? generalInfoData;
        const mergedGeneral = mergeGeneralSavePayload(serverRow, latestGeneral);
        const payload = { ...mergedGeneral, current_stage: String(nextStage) };
        if (latestGeneral?.thumbnail_image instanceof File) {
          payload.thumbnail_image = latestGeneral.thumbnail_image;
        }
        if (Array.isArray(payload.pickup_locations)) {
          payload.pickup_locations = serializePickupLocationsForSave(payload.pickup_locations);
        }

        const formData = new FormData();
        Object.keys(payload).forEach((key) => {
          const value = payload[key];
          if (
            value !== null &&
            value !== undefined &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            !(value instanceof File)
          ) {
            formData.append(key, JSON.stringify(value));
            return;
          }
          if (Array.isArray(value)) {
            if (key === "pickup_locations") {
              formData.append(key, JSON.stringify(value));
              return;
            }
            value.forEach((v) => {
              if (v !== null && v !== undefined && String(v) !== "") {
                formData.append(`${key}[]`, v);
              }
            });
            return;
          }
          if (value !== null && value !== undefined) {
            if (key === "thumbnail_image" && typeof value === "string" && value.startsWith("http")) {
              return;
            }
            formData.append(key, value);
          }
        });

        if (currentId) {
          await updateMutation.mutateAsync({ id: currentId, data: formData });
          showToast("success", "General Info saved.");
          setLastSavedAt(new Date());
          scrollAfterSave("gallery");
        } else {
          if (createMutation.isPending) return;
          await createMutation.mutateAsync(formData);
        }
      } else if (sectionId === "gallery") {
        const nextStage = Math.max(savedStage, RENTAL_STAGES.PRICING);
        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", "Gallery saved.");
        setLastSavedAt(new Date());
        scrollAfterSave("pricing");
      } else if (sectionId === "pricing") {
        const pricingBasis = getCategoryPricingBasis(rentalData?.data?.category);
        if (!isRentalPricingComplete(pricingFormDataRef.current, pricingBasis)) {
          showToast(
            "error",
            pricingBasis === RENTAL_PRICING_BASIS.DAY
              ? "Price per day is required."
              : pricingBasis === RENTAL_PRICING_BASIS.HOUR
              ? "Price per hour is required."
              :             pricingBasis === RENTAL_PRICING_BASIS.HYBRID
              ? "Price per hour and price per day are required."
              : "Price per hour and price per day are required."
          );
          return;
        }
        const nextStage = Math.max(savedStage, RENTAL_STAGES.AVAILABILITY);
        const latestPricing = normalizeRentalPricingPayload(
          pricingFormDataRef.current ?? pricingData,
          pricingBasis
        );
        const payload = { ...latestPricing, current_stage: String(nextStage) };
        [
          "price_per_hour",
          "price_per_day",
          "security_deposit",
          "price_per_km",
          "price_per_week",
          "price_per_month",
          "advance_value",
          "discount_value",
        ].forEach((key) => {
          const v = payload[key];
          if (v === "" || v === undefined) payload[key] = null;
        });
        if (payload.advance_type === "" || payload.advance_type === undefined) {
          payload.advance_type = null;
          payload.advance_value = null;
        }
        if (payload.discount_type === "" || payload.discount_type === undefined) {
          payload.discount_type = null;
          payload.discount_value = null;
        }
        const hasPricingRule = Boolean(rentalData?.data?.pricing_rule);
        await pricingUpdateMutation.mutateAsync({
          id: currentId,
          data: payload,
          isCreate: !hasPricingRule,
        });
        showToast("success", "Pricing & Deposit saved.");
        setLastSavedAt(new Date());
        scrollAfterSave("availability");
      } else if (sectionId === "availability") {
        const nextStage = Math.max(savedStage, RENTAL_STAGES.FAQ);
        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", "Stop Sale skipped.");
        setLastSavedAt(new Date());
        scrollAfterSave("faq");
      } else if (sectionId === "faq") {
        const nextStage = Math.max(savedStage, RENTAL_STAGES.INCLUSIONS);
        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", "FAQs skipped.");
        setLastSavedAt(new Date());
        scrollAfterSave("inclusions");
      } else if (sectionId === "inclusions") {
        const saveHandler = saveHandlersRef.current.inclusions;
        if (saveHandler) {
          await saveHandler();
        }
        const nextStage = Math.max(savedStage, RENTAL_STAGES.EXCLUSIONS);
        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", "Inclusions skipped.");
        setLastSavedAt(new Date());
        scrollAfterSave("exclusions");
      } else if (sectionId === "exclusions") {
        const saveHandler = saveHandlersRef.current.exclusions;
        if (saveHandler) {
          await saveHandler();
        }
        const nextStage = Math.max(savedStage, RENTAL_STAGES.TERMS);
        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", "Exclusions skipped.");
        setLastSavedAt(new Date());
        scrollAfterSave("terms");
      } else if (sectionId === "terms") {
        if (Boolean(stepValidity.terms)) {
          const saveHandler = saveHandlersRef.current.terms;
          if (saveHandler) {
            await saveHandler();
            await queryClient.invalidateQueries(["rentalTerms", currentId]);
          }
        }
        if (currentId) {
          await stageMutation.mutateAsync({ id: currentId, nextStage: RENTAL_MAX_STAGE });
        }
        showToast("success", isEditingExisting ? "Rental saved successfully!" : "Rental setup completed!");
        setLastSavedAt(new Date());
        setTimeout(handleClose, isEditingExisting ? 800 : 1500);
      } else {
        const saveHandler = saveHandlersRef.current[sectionId];
        if (saveHandler) {
          await saveHandler();
          await Promise.all([
            queryClient.invalidateQueries(["rentalFAQs", currentId]),
            queryClient.invalidateQueries(["rentalTerms", currentId]),
          ]);
        }

        if (!isStepComplete(sectionId)) {
          showToast("error", "Complete this step to proceed.");
          return;
        }

        const currentIndex = visibleSections.findIndex((item) => item.id === sectionId);
        const currentStage = getTabStage(sectionId, visibleSections);
        const nextStage = Math.max(savedStage, currentStage + 1);

        if (currentId && nextStage > savedStage) {
          await stageMutation.mutateAsync({ id: currentId, nextStage });
        }
        showToast("success", `${sectionName} saved.`);
        setLastSavedAt(new Date());

        if (currentIndex < visibleSections.length - 1) {
          scrollAfterSave(visibleSections[currentIndex + 1].id);
        }
      }
      clearSectionDirty(sectionId);
    } catch (e) {
      console.error(e);
      showToast(
        "error",
        e?.response?.data?.message || e?.message || "Something went wrong. Please try again."
      );
    } finally {
      skipAutoAdvanceRef.current = false;
      setIsSubmitting(false);
      setSavingSectionId(null);
    }
  };

  const handleUnsavedSaveAndContinue = async () => {
    await handleSectionSave(activeTab, { skipAutoAdvance: true });
    completePendingNavigation();
  };

  const handleClose = () => {
    setCreatedRentalId(null);
    setGeneralInfoData(null);
    setActiveTab("general");
    if (onClose) {
      onClose();
      return;
    }
    setExitTo(exitPath);
  };

  const handleSectionNavClick = (sectionId) => {
    const item = visibleSections.find((s) => s.id === sectionId);
    if (item && isSectionDisabled(item)) {
      showToast("warning", "Complete earlier sections first to unlock this stage");
    }
    requestSectionNavigation(sectionId, () => {
      scrollToSection(sectionId, { userInitiated: true });
    });
  };

  const renderSectionCard = (item, children) => {
    const isDisabledByNoId = !currentId && item.id !== "general";
    const isDisabledByStage = !isEditingExisting && item.stage > savedStage && item.id !== "general";
    const isDisabled = isDisabledByNoId || isDisabledByStage;
    const isActive = activeTab === item.id;
    const isCompleted = isSectionCompleted(savedStage, item.stage, hasTerms);

    return (
      <section
        key={item.id}
        id={`section-${item.id}`}
        data-section-id={item.id}
        ref={(element) => {
          sectionRefs.current[item.id] = element;
        }}
        className={`scroll-mt-24 rounded-lg border bg-white transition-colors duration-200 ${
          isActive ? "border-2 border-primary shadow-sm" : "border-gray-200"
        }`}
      >
        <button
          type="button"
          onClick={() => handleSectionNavClick(item.id)}
          className="w-full border-b border-gray-100 px-4 py-3.5 text-left sm:px-5"
        >
          <div className="flex items-center gap-3">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                isCompleted
                  ? "bg-emerald-50 text-emerald-600"
                  : isActive
                  ? "bg-primary/10 text-primary"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {isCompleted ? (
                <i className="fi fi-rr-check text-[10px]"></i>
              ) : (
                item.stage
              )}
            </span>
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <h3
                className={`truncate text-sm font-bold ${
                  isActive ? "text-gray-900" : "text-gray-800"
                }`}
              >
                {item.name}
              </h3>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {item.essential === false && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  Optional
                </span>
              )}
              {isDisabled && (
                <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-amber-600">
                  <i className="fi fi-rr-lock text-[9px]"></i>
                  Locked
                </span>
              )}
            </div>
          </div>
        </button>

        <div className="relative px-4 py-5 sm:px-5 sm:py-6">
          {isDisabled && (
            <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/80 px-4 py-3 text-sm text-amber-900">
              <i className="fi fi-rr-info relative top-0.5 text-amber-600"></i>
              <span>{getSectionDisabledMessage(item, isDisabledByNoId)}</span>
            </div>
          )}
          <div
            className={isDisabled ? "pointer-events-none opacity-45" : ""}
            {...getSectionCaptureProps(item.id)}
          >
            {children}
          </div>
        </div>
      </section>
    );
  };

  const renderContent = (item) => {
    switch (item.id) {
      case "general":
        return (
          <RentalGeneralInfo
            setData={setGeneralInfoData}
            formDataRef={generalFormDataRef}
            rentalId={currentId}
            validationErrors={validationErrors}
            submitAttempted={submitAttempted}
            onValidityChange={setIsGeneralValid}
          />
        );
      case "gallery":
        return (
          <RentalGallery
            rentalId={currentId}
            onValidityChange={(v) => setStepValidity((prev) => ({ ...prev, gallery: v }))}
          />
        );
      case "pricing":
        return (
          <RentalPricing
            setData={setPricingData}
            formDataRef={pricingFormDataRef}
            rentalId={currentId}
            validationErrors={validationErrors}
            onValidityChange={(v) => setStepValidity((prev) => ({ ...prev, pricing: v }))}
            compact
          />
        );
      case "availability":
        return <RentalBlockedDates rentalId={currentId} />;
      case "faq":
        return <RentalFAQ rentalId={currentId} />;
      case "inclusions":
        return (
          <RentalInclusions
            rentalId={currentId}
            registerSave={registerStepSave("inclusions")}
            onValidityChange={registerStepValidity("inclusions")}
          />
        );
      case "exclusions":
        return (
          <RentalExclusions
            rentalId={currentId}
            registerSave={registerStepSave("exclusions")}
            onValidityChange={registerStepValidity("exclusions")}
          />
        );
      case "terms":
        return (
          <RentalTerms
            rentalId={currentId}
            registerSave={registerStepSave("terms")}
            onValidityChange={registerStepValidity("terms")}
          />
        );
      default:
        return null;
    }
  };

  if (exitTo) {
    return <Navigate to={exitTo} replace />;
  }

  if (!open) return null;

  if (rentalIdProp && isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }
  if (rentalIdProp && error) {
    return <div className="p-6 text-sm text-red-500">Error: {error.message}</div>;
  }
  if (rentalIdProp && !rental && !isLoading) {
    return <div className="p-6 text-sm text-gray-500">Not found.</div>;
  }

  const renderApprovalBadge = () => {
    if (!rental || !showApprovalActions) return null;
    const ap = Number(rental.is_approved);
    if (ap === 1) {
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
          <i className="fi fi-rr-check mr-1.5"></i>
          Approved
        </span>
      );
    }
    if (ap === 2) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 ring-1 ring-red-200">
          <i className="fi fi-rr-cross mr-1.5"></i>
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-50 px-3 py-1 text-xs font-medium text-yellow-800 ring-1 ring-yellow-200">
        <i className="fi fi-rr-clock mr-1.5"></i>
        Pending Approval
      </span>
    );
  };

  return (
    <div className="min-h-full bg-slate-50">
      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              <i className="fi fi-rr-arrow-left relative top-0.5"></i>
              <span className="hidden sm:inline">{backLabel}</span>
            </button>
            <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">
              {currentId ? "Edit Rental" : "New Rental"}
            </h1>
          </div>
          <div className="flex shrink-0 items-center gap-3 sm:gap-4">
            {renderApprovalBadge()}
            {showApprovalActions && currentId ? (
              <div className="hidden gap-2 sm:flex">
                <button
                  type="button"
                  className="rounded-xl bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                  onClick={() => approveMutation.mutate(1)}
                  disabled={approveMutation.isPending}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
                  onClick={() => approveMutation.mutate(2)}
                  disabled={approveMutation.isPending}
                >
                  Reject
                </button>
              </div>
            ) : null}
            <RentalCompletionTracker savedStage={savedStage} hasTerms={hasTerms} compact wide />
          </div>
        </div>
        <div className="border-t border-gray-100 px-4 py-2 lg:hidden">
          <RentalBuilderNav
            items={visibleSections}
            activeSectionId={activeTab}
            savedStage={savedStage}
            hasRentalId={!!currentId}
            hasTerms={hasTerms}
            isEditingExisting={isEditingExisting}
            onSectionClick={handleSectionNavClick}
            orientation="horizontal"
          />
        </div>
      </div>

      <div className="mx-auto flex w-full gap-5 px-4 py-4 sm:px-6 lg:py-5">
        <aside className="relative z-40 hidden w-72 shrink-0 self-start lg:sticky lg:top-20 lg:block">
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Sections
            </p>
            <RentalBuilderNav
              items={visibleSections}
              activeSectionId={activeTab}
              savedStage={savedStage}
              hasRentalId={!!currentId}
              hasTerms={hasTerms}
              isEditingExisting={isEditingExisting}
              onSectionClick={handleSectionNavClick}
            />
          </div>
        </aside>

        <main ref={contentRef} className="relative min-w-0 flex-1 pb-4">
          <div className="space-y-8">
            {Number(rental?.is_approved) === 2 && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
                role="status"
              >
                <p className="font-semibold">This listing was rejected by an admin.</p>
                <p className="mt-1 text-red-800/90">
                  Update the rental as needed, then go to{" "}
                  <span className="font-medium">Terms &amp; Conditions</span> and click{" "}
                  <span className="font-medium">Save &amp; Finish</span> to send it back for approval.
                </p>
              </div>
            )}

            {visibleSections.map((item) => renderSectionCard(item, renderContent(item)))}
          </div>
        </main>
      </div>

      <PackageActionBar
        activeSectionLabel={activeSectionLabel}
        saveLabel={actionBarSaveLabel}
        isSubmitting={
          isSubmitting ||
          savingSectionId !== null ||
          createMutation.isPending ||
          updateMutation.isPending ||
          pricingUpdateMutation.isPending
        }
        isDisabled={isSaveDisabled(activeTab)}
        isLastSection={activeTab === "terms"}
        lastSavedAt={lastSavedAt}
        onSave={() => handleSectionSave(activeTab)}
        onClose={handleClose}
      />

      <UnsavedSectionDialog
        open={unsavedDialogOpen}
        sectionLabel={activeSectionLabel}
        onStay={cancelPendingNavigation}
        onSaveAndContinue={handleUnsavedSaveAndContinue}
        isSaving={isSubmitting || savingSectionId !== null}
      />

      {showToster && (
        <Toster
          key={toastKey}
          message={tosterMessage}
          type={tosterType}
          onClose={() => setShowToster(false)}
        />
      )}
    </div>
  );
};

export default AdminRentalBuilder;
